import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { 
  MessageCircle, 
  Send, 
  Loader2, 
  Ticket, 
  CheckCircle, 
  AlertCircle,
  Bot,
  User,
  ChevronDown,
  Coins
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

// Use current origin for API calls
const API = `${window.location.origin}/api`;

const TICKET_COST = 2; // credits

const SupportPanel = ({ projectId, designId, onCreditsUsed }) => {
  const [activeChat, setActiveChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [showTicketForm, setShowTicketForm] = useState(false);
  const [ticketSubject, setTicketSubject] = useState('');
  const [ticketPriority, setTicketPriority] = useState('medium');
  const [creatingTicket, setCreatingTicket] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const startChat = async () => {
    if (!inputMessage.trim()) return;
    
    setSending(true);
    try {
      const response = await axios.post(`${API}/support/chat/start`, {
        project_id: projectId,
        design_id: designId,
        initial_message: inputMessage
      }, { withCredentials: true });
      
      setActiveChat(response.data.chat_id);
      setMessages(response.data.messages);
      setInputMessage('');
    } catch (error) {
      console.error('Error starting chat:', error);
      toast.error('Failed to start support chat');
    } finally {
      setSending(false);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || !activeChat) return;
    
    // Optimistically add user message
    const userMsg = {
      role: 'user',
      content: inputMessage,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMsg]);
    setInputMessage('');
    setSending(true);
    
    try {
      const response = await axios.post(`${API}/support/chat/message`, {
        chat_id: activeChat,
        message: userMsg.content
      }, { withCredentials: true });
      
      // Add AI response
      setMessages(prev => [...prev, response.data.messages[1]]);
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
      // Remove optimistic message on error
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setSending(false);
    }
  };

  const createTicket = async () => {
    if (!ticketSubject.trim() || !activeChat) return;
    
    setCreatingTicket(true);
    try {
      const response = await axios.post(`${API}/support/ticket/create`, {
        chat_id: activeChat,
        subject: ticketSubject,
        priority: ticketPriority
      }, { withCredentials: true });
      
      toast.success('Support ticket created! Our team will respond within 24 hours.');
      setShowTicketForm(false);
      setTicketSubject('');
      
      // Notify parent about credit usage
      if (onCreditsUsed) {
        onCreditsUsed(response.data.credits_used);
      }
    } catch (error) {
      console.error('Error creating ticket:', error);
      toast.error(error.response?.data?.detail || 'Failed to create ticket');
    } finally {
      setCreatingTicket(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (activeChat) {
        sendMessage();
      } else {
        startChat();
      }
    }
  };

  return (
    <div className="flex flex-col h-full" data-testid="support-panel">
      {/* Chat Messages Area */}
      <div className="flex-1 overflow-y-auto max-h-[350px] space-y-3 mb-4 pr-1">
        {messages.length === 0 ? (
          <div className="text-center py-8">
            <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center mx-auto mb-3">
              <MessageCircle className="w-6 h-6 text-indigo-600" />
            </div>
            <h4 className="font-medium text-zinc-900 mb-1">Need Help?</h4>
            <p className="text-sm text-zinc-500 mb-4">
              Ask our AI assistant about your design, features, or any issues you&apos;re facing.
            </p>
          </div>
        ) : (
          messages.map((msg, idx) => (
            <div
              key={idx}
              className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              {msg.role === 'assistant' && (
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center flex-shrink-0">
                  <Bot className="w-4 h-4 text-white" />
                </div>
              )}
              <div
                className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                  msg.role === 'user'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-zinc-100 text-zinc-800'
                }`}
              >
                {msg.content}
              </div>
              {msg.role === 'user' && (
                <div className="w-7 h-7 rounded-full bg-zinc-200 flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-zinc-600" />
                </div>
              )}
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="space-y-3">
        <div className="flex gap-2">
          <Textarea
            data-testid="support-input"
            placeholder={activeChat ? "Type your message..." : "Describe your question or issue..."}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            className="min-h-[80px] bg-white text-zinc-900 placeholder:text-zinc-400 border-zinc-300 resize-none"
            disabled={sending}
          />
        </div>
        
        <div className="flex gap-2">
          <Button
            data-testid="send-support-message-btn"
            onClick={activeChat ? sendMessage : startChat}
            disabled={sending || !inputMessage.trim()}
            className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            {sending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {activeChat ? 'Sending...' : 'Starting...'}
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                {activeChat ? 'Send' : 'Start Chat'}
              </>
            )}
          </Button>
        </div>

        {/* Create Ticket Section */}
        {activeChat && messages.length >= 2 && (
          <div className="pt-3 border-t border-zinc-200">
            {!showTicketForm ? (
              <button
                onClick={() => setShowTicketForm(true)}
                className="w-full flex items-center justify-center gap-2 py-2 text-sm text-zinc-600 hover:text-indigo-600 transition-colors"
                data-testid="show-ticket-form-btn"
              >
                <Ticket className="w-4 h-4" />
                <span>Need more help? Create a support ticket</span>
                <ChevronDown className="w-3 h-3" />
              </button>
            ) : (
              <div className="space-y-3 p-3 bg-zinc-50 rounded-lg" data-testid="ticket-form">
                <div className="flex items-center gap-2 text-sm font-medium text-zinc-900">
                  <Ticket className="w-4 h-4 text-indigo-600" />
                  Create Support Ticket
                </div>
                
                <Input
                  data-testid="ticket-subject-input"
                  placeholder="Brief summary of your issue"
                  value={ticketSubject}
                  onChange={(e) => setTicketSubject(e.target.value)}
                  className="bg-white"
                />
                
                <div className="flex gap-2">
                  <select
                    value={ticketPriority}
                    onChange={(e) => setTicketPriority(e.target.value)}
                    className="flex-1 h-10 px-3 rounded-md border border-zinc-300 bg-white text-sm"
                    data-testid="ticket-priority-select"
                  >
                    <option value="low">Low Priority</option>
                    <option value="medium">Medium Priority</option>
                    <option value="high">High Priority</option>
                  </select>
                </div>

                <div className="flex items-center justify-between text-xs text-zinc-500">
                  <span className="flex items-center gap-1">
                    <Coins className="w-3 h-3" />
                    Costs {TICKET_COST} credits
                  </span>
                  <span>Response within 24 hours</span>
                </div>
                
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowTicketForm(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    data-testid="create-ticket-btn"
                    size="sm"
                    onClick={createTicket}
                    disabled={creatingTicket || !ticketSubject.trim()}
                    className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white"
                  >
                    {creatingTicket ? (
                      <>
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <Ticket className="w-3 h-3 mr-1" />
                        Create Ticket
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SupportPanel;
